import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { money, usdApprox, useCurrency } from "@/lib/currency";
import { useSalesTax } from "@/lib/tax";
import { BrandedLoader } from "@/components/util/BrandedLoader";
import { useCart } from "@/contexts/CartContext";
import { ArrowRight, Check, ShoppingCart, Star, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function ServiceConfiguratorPage() {
  useCurrency(); // re-render prices on display-currency change
  const { slug } = useParams();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [tierId, setTierId] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState(new Set());

  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => {
      setService(r.data);
      const popular = r.data.tiers.find((t) => t.popular) || r.data.tiers[0];
      if (popular) setTierId(popular.id);
    });
  }, [slug]);

  const tier = useMemo(() => service?.tiers.find((t) => t.id === tierId), [service, tierId]);
  const addonItems = useMemo(
    () => (service?.addons || []).filter((a) => selectedAddons.has(a.id)),
    [service, selectedAddons]
  );
  const total = useMemo(() => {
    const base = tier?.price || 0;
    const ads = addonItems.reduce((s, a) => s + a.price, 0);
    return base + ads;
  }, [tier, addonItems]);
  // Site-wide tax — shared source with Checkout via /api/settings.
  const { taxRate, taxLabel, compute: computeTax } = useSalesTax();
  const { taxAmount, total: totalWithTax } = computeTax(total);

  const toggleAddon = (id) => {
    const next = new Set(selectedAddons);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedAddons(next);
  };

  const handleAddToCart = () => {
    if (!tier) return;
    addItem({
      kind: "service",
      name: `${service.name} — ${tier.name}`,
      price: total,
      service_slug: service.slug,
      line_type: "tier",
      item_code: tier.item_code || "",
      item_id: tier.id,
      quantity: 1,
      parent_service_id: service.id,
      selected_addons: addonItems.map((a) => ({
        item_code: a.item_code || "",
        item_id: a.id,
        quantity: 1,
      })),
      meta: {
        slug: service.slug,
        tier: tier.name,
        addons: addonItems.map((a) => a.name),
      },
    });
    toast.success("Custom package added to cart");
  };

  if (!service) {
    return <BrandedLoader testid="service-configurator-loading" />;
  }

  return (
    <div data-testid={`configurator-${service.slug}`} className="bg-[#0F0F0F]">
      {/* HERO */}
      <section className="relative overflow-hidden grain border-b border-[#1F1F1F]">
        <div
          aria-hidden
          className="absolute -top-32 right-0 w-[600px] h-[600px] rounded-full opacity-25 blur-[140px]"
          style={{ background: "radial-gradient(circle, #FF6600 0%, transparent 60%)" }}
        />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div>
            <Link to="/services" className="ur-eyebrow inline-block hover:text-white transition-colors">
              ← All Services
            </Link>
            <h1 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02]">
              {service.name}
            </h1>
            <p className="mt-4 text-[#B5B5B5] text-base sm:text-lg max-w-xl leading-relaxed">
              {service.long_description || service.short_description}
            </p>
          </div>
          <div className="relative">
            <div className="aspect-[5/4] rounded-2xl overflow-hidden border border-[#1F1F1F]">
              <img src={service.image} alt={service.name} className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* CONFIGURATOR */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-20 grid lg:grid-cols-[1fr_360px] gap-10 items-start">
        <div className="space-y-14">
          {/* Step 1 */}
          <div>
            <StepHeader n={1} title="Choose Your Tier" sub="Pick the base package that fits your goals." />
            <div className="mt-8 grid md:grid-cols-3 gap-5">
              {service.tiers.map((t) => {
                const active = t.id === tierId;
                return (
                  <button
                    key={t.id}
                    data-testid={`tier-${t.name.toLowerCase()}`}
                    onClick={() => setTierId(t.id)}
                    className={`text-left ur-card p-6 transition-all ${
                      active ? "border-[#FF6600] ring-2 ring-[#FF6600]/30" : "hover:border-[#FF6600]/40"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-display text-2xl font-bold">{t.name}</div>
                      {t.popular && (
                        <span className="text-[10px] font-bold uppercase tracking-widest bg-[#FF6600] text-black px-2 py-1 rounded-full flex items-center gap-1">
                          <Star className="w-3 h-3" /> Popular
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#A3A3A3] mt-2">{t.description}</div>
                    <div className="mt-5 font-display text-3xl font-black">{money(t.price)}</div>
                    <div className="text-xs text-[#A3A3A3] mt-1">{usdApprox(t.price)}</div>
                    {t.features?.length > 0 && (
                      <ul className="mt-5 space-y-2 text-sm">
                        {t.features.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-[#D1D1D1]">
                            <Check className="w-3.5 h-3.5 text-[#FF6600] mt-1 shrink-0" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    )}
                    <div
                      className={`mt-5 inline-flex items-center gap-2 text-xs font-bold ${
                        active ? "text-[#FF6600]" : "text-[#A3A3A3]"
                      }`}
                    >
                      {active ? "Selected" : "Select Package"}{" "}
                      {active && <Check className="w-3.5 h-3.5" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 2 */}
          {service.addons?.length > 0 && (
            <div>
              <StepHeader n={2} title="Premium Add-ons" sub="Pick optional extras to enhance your package." />
              <div className="mt-8 grid sm:grid-cols-2 gap-3">
                {service.addons.map((a) => {
                  const active = selectedAddons.has(a.id);
                  return (
                    <button
                      key={a.id}
                      data-testid={`addon-${a.id}`}
                      onClick={() => toggleAddon(a.id)}
                      className={`text-left ur-card p-5 flex items-start gap-4 transition-all ${
                        active ? "border-[#FF6600] bg-[#1A1209]" : "hover:border-[#FF6600]/40"
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded-md border grid place-items-center shrink-0 mt-0.5 ${
                          active ? "bg-[#FF6600] border-[#FF6600]" : "border-[#444]"
                        }`}
                      >
                        {active && <Check className="w-3.5 h-3.5 text-black" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3">
                          <div className="font-semibold">{a.name}</div>
                          <div className="font-display font-bold text-[#FF6600] shrink-0">
                            +{money(a.price)}
                          </div>
                        </div>
                        {a.description && (
                          <div className="text-xs text-[#A3A3A3] mt-1">{a.description}</div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        <aside data-testid="configurator-summary" className="lg:sticky lg:top-28 ur-card p-6">
          <div className="ur-eyebrow">Step 3 · Summary</div>
          <h3 className="font-display mt-3 text-2xl font-bold">{service.name}</h3>
          <div className="mt-6 space-y-3 text-sm">
            <Row label={`Tier · ${tier?.name || "—"}`} value={tier ? `${money(tier.price)}` : "—"} />
            {addonItems.map((a) => (
              <Row key={a.id} label={a.name} value={`+${money(a.price)}`} muted />
            ))}
          </div>
          <div className="mt-6 pt-6 border-t border-[#262626] space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-[#A3A3A3]">Subtotal</span>
              <span data-testid="configurator-subtotal" className="font-semibold text-white tabular-nums">{money(total)}</span>
            </div>
            {taxRate > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-[#A3A3A3]" data-testid="configurator-tax-label">{taxLabel}</span>
                <span data-testid="configurator-tax-amount" className="font-semibold text-white tabular-nums">{money(taxAmount)}</span>
              </div>
            )}
            <div className="pt-2 border-t border-[#262626] flex items-end justify-between">
              <span className="text-white font-semibold">Estimated Total</span>
              <div className="text-right">
                <span data-testid="configurator-total" className="font-display text-3xl font-black block leading-none">
                  {money(totalWithTax)}
                </span>
                <span className="text-xs text-[#A3A3A3] mt-1 inline-block">{usdApprox(totalWithTax)}</span>
              </div>
            </div>
          </div>
          <button
            data-testid="configurator-add-to-cart"
            onClick={handleAddToCart}
            disabled={!tier}
            className="ur-btn-primary w-full mt-6 disabled:opacity-40"
          >
            Add to Cart <ShoppingCart className="w-4 h-4" />
          </button>
          <button
            data-testid="configurator-checkout"
            onClick={() => {
              handleAddToCart();
              navigate("/checkout");
            }}
            className="ur-btn-secondary w-full mt-3"
          >
            Continue to Checkout <ArrowRight className="w-4 h-4" />
          </button>
          <div className="mt-5 flex items-start gap-2 text-xs text-[#A3A3A3]">
            <Sparkles className="w-3.5 h-3.5 text-[#FF6600] mt-0.5 shrink-0" />
            Custom configuration is saved to your cart. You can edit before checkout.
          </div>
        </aside>
      </section>
    </div>
  );
}

const StepHeader = ({ n, title, sub }) => (
  <div className="flex items-center gap-4">
    <div className="w-11 h-11 rounded-full bg-[#FF6600] text-black font-display font-black grid place-items-center">
      {n}
    </div>
    <div>
      <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{title}</h2>
      <p className="text-sm text-[#A3A3A3] mt-1">{sub}</p>
    </div>
  </div>
);

const Row = ({ label, value, muted }) => (
  <div className="flex items-start justify-between gap-4">
    <span className={muted ? "text-[#A3A3A3]" : "text-white"}>{label}</span>
    <span className={`font-semibold ${muted ? "text-[#D1D1D1]" : "text-white"}`}>{value}</span>
  </div>
);
